import type { ShipDto } from '@/api/hooks'

/** Canonical ship-type order for grouping the fleet: light → heavy → apex. */
export const SHIP_TYPE_ORDER = ['brick', 'fregate', 'galion', 'legendary']

export interface ShipGroup {
  type: string
  label: string
  ships: ShipDto[]
}

/**
 * Buckets ships by their type, preserving each bucket's incoming order (so the
 * caller's status sort survives within a type), and orders the buckets
 * light → apex. Unknown types sort last. Powers the collapsible by-type
 * sections on the fleet page (so a captain can fold away everything but, say,
 * their Frégates).
 */
export function groupShipsByType(ships: ShipDto[]): ShipGroup[] {
  const byType = new Map<string, ShipDto[]>()
  for (const ship of ships) {
    const key = ship.shipType ?? 'brick'
    const bucket = byType.get(key)
    if (bucket) bucket.push(ship)
    else byType.set(key, [ship])
  }

  const rank = (t: string): number => {
    const i = SHIP_TYPE_ORDER.indexOf(t)
    return i === -1 ? SHIP_TYPE_ORDER.length : i
  }

  return [...byType.entries()]
    .map(([type, group]) => ({ type, label: group[0]?.shipTypeLabel ?? type, ships: group }))
    .sort((a, b) => rank(a.type) - rank(b.type))
}

/**
 * Taille maximale d'une flotte de mission : 2 navires par niveau de Chantier
 * naval, plancher à 1. Miroir EXACT de `FleetRules::missionFleetCap`
 * (api/src/Domain/Fleet/FleetRules.php:39). Une seule copie côté front, contre
 * neuf avant ce lot.
 *
 * `null`/`undefined` (flotte pas encore chargée, ou aucun Chantier naval) vaut
 * le niveau 0, donc cap 1 — comme le serveur. Les neuf copies écrivaient
 * `?? 1`, soit cap 2 : un cran de plus que ce que le serveur accepte. En
 * pratique inerte (sans Chantier naval le joueur n'a aucun navire, et le picker
 * affiche son état vide), mais centraliser le mauvais défaut l'aurait gravé.
 */
export function missionFleetCap(shipyardLevel: number | null | undefined): number {
  return Math.max(1, (shipyardLevel ?? 0) * 2)
}
